import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { scriptsApi } from '@/services/api/scripts'
import { datasetsApi } from '@/services/api/datasets'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Play, ArrowRight, AlertCircle } from 'lucide-react'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { showSuccessToast } from '@/utils/error-handler'
import BacktestButton from '@/components/backtest/BacktestButton'

export default function BacktestRunPage() {
  const navigate = useNavigate()
  const [selectedScriptId, setSelectedScriptId] = useState<string>('')
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>('')

  const { data: scriptsData, isLoading: scriptsLoading } = useQuery({
    queryKey: ['scripts'],
    queryFn: async () => {
      const result = await scriptsApi.list()
      return result.scripts
    },
  })

  const { data: datasetsData, isLoading: datasetsLoading } = useQuery({
    queryKey: ['datasets'],
    queryFn: async () => {
      const result = await datasetsApi.list()
      return result.datasets
    },
  })

  const isLoading = scriptsLoading || datasetsLoading

  if (isLoading) {
    return <LoadingPage />
  }

  const readyDatasets = datasetsData?.filter(d => d.status === 'ready') || []
  const compiledScripts = scriptsData?.filter(s => !!s.compiledSql) || []

  const handleJobCreated = (jobId: string) => {
    showSuccessToast('Backtest job created successfully')
    navigate(`/jobs/${jobId}`)
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Run Backtest</h1>
        <p className="text-muted-foreground">
          Select a script and dataset to run a backtest. Make sure the script is compiled and has backtest configuration.
        </p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Backtest Configuration</CardTitle>
          <CardDescription>
            Choose a script and dataset to execute the backtest
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="script-select">Script</Label>
            <Select value={selectedScriptId} onValueChange={setSelectedScriptId}>
              <SelectTrigger id="script-select">
                <SelectValue placeholder="Select a script" />
              </SelectTrigger>
              <SelectContent>
                {compiledScripts.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No compiled scripts available
                  </div>
                ) : (
                  compiledScripts.map(script => (
                    <SelectItem key={script.id} value={script.id}>
                      {script.name || 'Untitled Script'}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {compiledScripts.length === 0 && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                You need at least one compiled script to run a backtest.
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto"
                  onClick={() => navigate('/scripts')}
                >
                  Go to Scripts
                </Button>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="dataset-select">Dataset</Label>
            <Select value={selectedDatasetId} onValueChange={setSelectedDatasetId}>
              <SelectTrigger id="dataset-select">
                <SelectValue placeholder="Select a dataset" />
              </SelectTrigger>
              <SelectContent>
                {readyDatasets.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No ready datasets available
                  </div>
                ) : (
                  readyDatasets.map(dataset => (
                    <SelectItem key={dataset.id} value={dataset.id}>
                      {dataset.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {readyDatasets.length === 0 && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                You need at least one ready dataset to run a backtest.
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto"
                  onClick={() => navigate('/datasets')}
                >
                  Go to Datasets
                </Button>
              </p>
            )}
          </div>

          <div className="pt-4">
            {selectedScriptId && selectedDatasetId ? (
              <BacktestButton
                scriptId={selectedScriptId}
                selectedDatasetId={selectedDatasetId}
                onJobCreated={handleJobCreated}
                className="w-full"
              />
            ) : (
              <Button
                disabled
                className="w-full"
              >
                <Play className="w-4 h-4 mr-2" />
                Run Backtest
              </Button>
            )}
          </div>

          {selectedScriptId && (
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/scripts/${selectedScriptId}`, { state: { openBacktestConfig: true } })}
              >
                Configure Backtest Settings
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
